import { configureStore } from "@reduxjs/toolkit";
import { captureException } from "@sentry/react";
import { Reducer, ReducersMapObject, Store } from "redux";
import createSagaMiddleware, { Saga } from "redux-saga";

import { isLocal, isSentryEnabled } from "@/util/environments";

import createRootReducer, { LazyState } from "./rootReducer";
import rootSaga, { Context } from "./rootSaga";

export interface InjectableStore extends Store {
  injectReducer: (key: keyof LazyState, reducer: Reducer) => void;
  injectSaga: (key: keyof LazyState, saga: Saga) => void;
}

const setupStore = (sagaContext: Context) => {
  const sagaMiddleware = createSagaMiddleware({
    onError: (error, { sagaStack }) => {
      if (isLocal()) {
        console.error(error); // eslint-disable-line no-console
        console.error(sagaStack); // eslint-disable-line no-console
      }
      if (isSentryEnabled()) {
        captureException(error, (scope) => {
          scope.addBreadcrumb({
            type: "error",
            level: "error",
            category: "exception",
            message: sagaStack
          });
          return scope;
        });
      }
    }
  });

  const store = {
    ...configureStore({
      reducer: createRootReducer(),
      middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
          serializableCheck: {
            ignoredActionPaths: ["payload"]
          }
        }).concat(sagaMiddleware)
    }),
    injectSaga(key: keyof LazyState, saga: Saga) {
      if (!this.lazySagas.has(key)) {
        this.lazySagas.add(key);
        sagaMiddleware.run(saga);
      }
    },
    lazyReducers: {} as ReducersMapObject<LazyState> | {},
    lazySagas: new Set<keyof LazyState>()
  };

  sagaMiddleware.run(rootSaga, sagaContext);

  return store;
};

export default setupStore;
